/**
 * The frame every screen lives in: header, offline banner, scroll area and the
 * FIVE-TAB bottom navigation.
 *
 * FIVE TABS, EXACTLY. The spec calls out "một trang dài với hàng chục nút" as
 * the thing to avoid, and names the five destinations: Trang chủ, Công việc,
 * Quét QR, Thông báo, Cá nhân. Everything else in the app is reached FROM one
 * of these five, never bolted on as a sixth tab. If a future feature needs a
 * home, it goes inside Cá nhân — not next to Quét QR.
 *
 * Quét QR sits in the MIDDLE slot on purpose: it is the most-used action on a
 * shift and the middle of the bar is the easiest target for either thumb.
 */
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { OfflineBanner, SyncStatusPill } from './SyncStatus';
import { useAuth } from '../auth/AuthProvider';

interface Tab {
  readonly to: string;
  readonly label: string;
  readonly icon: JSX.Element;
  readonly badgeKey?: 'notifications' | 'tasks';
}

/** Inline SVGs: no icon-font download on a phone with a slow connection. */
const icons = {
  home: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 11.5 12 4l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" strokeLinejoin="round" />
    </svg>
  ),
  tasks: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M9 5h9M9 12h9M9 19h9" strokeLinecap="round" />
      <path d="m3 5 1.5 1.5L7 4M3 12l1.5 1.5L7 11M3 19l1.5 1.5L7 18" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  scan: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3" strokeLinecap="round" />
      <path d="M4 12h16" strokeLinecap="round" />
    </svg>
  ),
  bell: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M6 9a6 6 0 1 1 12 0c0 4 1.5 5.5 1.5 5.5h-15S6 13 6 9z" strokeLinejoin="round" />
      <path d="M10 18a2 2 0 0 0 4 0" strokeLinecap="round" />
    </svg>
  ),
  person: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="8" r="3.5" />
      <path d="M4.5 20a7.5 7.5 0 0 1 15 0" strokeLinecap="round" />
    </svg>
  ),
} as const;

const TABS: readonly Tab[] = [
  { to: '/', label: 'Trang chủ', icon: icons.home },
  { to: '/cong-viec', label: 'Công việc', icon: icons.tasks, badgeKey: 'tasks' },
  { to: '/quet-qr', label: 'Quét QR', icon: icons.scan },
  { to: '/thong-bao', label: 'Thông báo', icon: icons.bell, badgeKey: 'notifications' },
  { to: '/ca-nhan', label: 'Cá nhân', icon: icons.person },
];

export interface ShellBadges {
  readonly notifications: number;
  readonly tasks: number;
}

export function AppShell({ badges }: { readonly badges: ShellBadges }): JSX.Element {
  const { me, primaryRoleName, branch } = useAuth();
  const location = useLocation();

  return (
    <div className="flex h-full flex-col bg-slate-100">
      <header
        className="sticky top-0 z-20 bg-brand-700 text-white"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-base font-bold leading-tight">{me?.user.fullName ?? 'Nhân viên'}</p>
            <p className="truncate text-xs text-brand-100">
              {primaryRoleName}
              {branch === null ? '' : ` · ${branch.name}`}
            </p>
          </div>
          <SyncStatusPill />
        </div>
        <OfflineBanner />
      </header>

      {/* key on pathname so a new screen always starts scrolled to the top. */}
      <main key={location.pathname} className="flex-1 overflow-y-auto px-4 pb-[calc(env(safe-area-inset-bottom,0px)+96px)] pt-4">
        <Outlet />
      </main>

      <BottomNav badges={badges} />
    </div>
  );
}

function BottomNav({ badges }: { readonly badges: ShellBadges }): JSX.Element {
  return (
    <nav
      aria-label="Điều hướng chính"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <ul className="grid grid-cols-5">
        {TABS.map((tab) => (
          <li key={tab.to}>
            <NavLink
              to={tab.to}
              // `end` only on the root so /cong-viec/:id keeps the tab active.
              end={tab.to === '/'}
              className={({ isActive }) =>
                [
                  'relative flex min-h-nav flex-col items-center justify-center gap-1 px-1 py-2',
                  'text-[11px] font-semibold active:bg-slate-100',
                  isActive ? 'text-brand-700' : 'text-slate-500',
                ].join(' ')
              }
            >
              {({ isActive }) => (
                <>
                  <span className={`h-6 w-6 ${isActive ? 'scale-110' : ''} transition-transform`}>
                    {tab.icon}
                  </span>
                  <span className="truncate">{tab.label}</span>
                  <Badge count={tab.badgeKey === undefined ? 0 : badges[tab.badgeKey]} />
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function Badge({ count }: { readonly count: number }): ReactNode {
  if (count <= 0) return null;
  return (
    <span className="absolute right-1/2 top-1 translate-x-4 rounded-full bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
      {count > 99 ? '99+' : count}
    </span>
  );
}
